import {
  Alert,
  Flex,
  Heading,
  IconWarningSolid,
  List,
  RadioInput,
  RadioInputGroup,
  Text,
  ToggleDetails,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import Skeleton from "react-loading-skeleton";
import { useLocation, useOutletContext } from "react-router";
import { Card } from "../../../../../components/Card";
import { useNotification } from "../../../../../components/CustomHooks/NotificationProvider";
import { AssignmentService } from "../../../../../services/AssignmentService";
import RatingScaleSelect from "../../../../../components/Selectors/RatingScaleSelect";
import { MasteryLevelContext } from "../../../../../utils/Context";
import { buildLMGTable } from "../utils";
import {
  buildOutcomeStatementMap,
  getOutcomeStaleness,
  sanitizeStatement,
} from "../../../../../components/LearningOutcomes/utils";

const LearningMasteryGradebook = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { setNextEnabled, setFormData, formData } = useOutletContext();
  const { ratingScales } = useContext(MasteryLevelContext);
  const location = useLocation();
  const section = location.state.section;

  const [roundingMethod, setRoundingMethod] = useState(
    formData.metadata?.roundingMethod || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [staleOutcomes, setStaleOutcomes] = useState([]);
  const [scaleSelectClearKey, setScaleSelectClearKey] = useState(0);

  useEffect(() => {
    if (formData.metadata.results && "roundingMethod" in formData.metadata) {
      const {
        outcomeColumns,
        studentRows,
        staleOutcomes: stale,
      } = buildLMGTable(
        formData.metadata.results,
        formData.metadata.roundingMethod
      );
      setColumns(outcomeColumns);
      setRows(studentRows);
      setStaleOutcomes(stale);
    }
  }, [formData.metadata.results]); // eslint-disable-line

  useEffect(() => {
    async function fetchLMG() {
      try {
        setLoading(true);
        const results = await AssignmentService.getResultsByContext(
          AssignmentService.ROLLUP,
          section.canvas_id
        );

        const statementMap = buildOutcomeStatementMap(
          section.learning_outcomes
        );

        const approved_lo_results = results["data"].map((entry) => {
          const scoredWithStaleness = entry["scores"].map((score) => {
            const description = sanitizeStatement(
              score["links"]["outcome_metadata"]["description"]
            );
            return {
              ...score,
              _staleness: getOutcomeStaleness(description, statementMap),
            };
          });
          return { ...entry, scores: scoredWithStaleness };
        });
        results["data"] = approved_lo_results;

        // Build outcome metadata from rollup data for the rating scale selector
        const outcomeMetadata = {};
        for (const rollup of results["data"]) {
          for (const score of rollup.scores || []) {
            const outcomeId = String(score.links?.outcome);
            if (!outcomeMetadata[outcomeId] && score.links?.outcome_metadata) {
              outcomeMetadata[outcomeId] = {
                title: score.links.outcome_metadata.title,
                pointsPossible: score.links.outcome_metadata.points_possible,
              };
            }
          }
        }

        setFormData((prev) => ({
          ...prev,
          metadata: {
            results: results,
            roundingMethod: "",
            outcomeMetadata,
            outcomeRatingScaleMap: {},
          },
        }));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    if (
      !formData.metadata?.results &&
      window.location.pathname === "/scores/home/lmg"
    ) {
      fetchLMG();
    }
  }, [section.canvas_id, showNotification, formData, setFormData]);

  function handleChangeRounding(_e, value) {
    setRoundingMethod(value);
    setScaleSelectClearKey((prev) => prev + 1);

    // Auto-select rating scales based on priority order
    const autoSelectedMap = autoSelectRatingScales(value);

    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        roundingMethod: value,
        outcomeRatingScaleMap: autoSelectedMap,
      },
    }));

    // Enable next if all outcomes have a scale auto-selected
    const outcomes = formData.metadata.outcomeMetadata;
    const allScalesSelected =
      !outcomes || Object.keys(outcomes).every((id) => !!autoSelectedMap[id]);
    setNextEnabled(!!value && allScalesSelected);

    const roundingString =
      value === "roundHalfUp" ? "_round_half_up" : "_round_down";

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "student") return col;
        const outcomeId = col.key.split("_")[0];
        return { ...col, key: outcomeId + roundingString };
      })
    );
  }

  function handleRatingScaleChange(outcomeId, mapId) {
    const updatedMap = {
      ...formData.metadata.outcomeRatingScaleMap,
      [outcomeId]: mapId,
    };
    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, outcomeRatingScaleMap: updatedMap },
    }));

    // Enable next only if rounding is selected AND all outcomes have a scale
    const outcomes = formData.metadata.outcomeMetadata;
    const allScalesSelected =
      !outcomes || Object.keys(outcomes).every((id) => !!updatedMap[id]);
    setNextEnabled(!!roundingMethod && allScalesSelected);
  }

  function getCompatibleMapIds(outcomeId) {
    const outcomes = formData.metadata.outcomeMetadata;
    if (!outcomes || !ratingScales) return undefined;

    // Get points_possible and collect rounded scores from rollup data
    let pointsPossible = null;
    const roundedScores = new Set();
    const rollupData = formData.metadata.results?.data || [];

    for (const rollup of rollupData) {
      for (const score of rollup.scores || []) {
        if (String(score.links?.outcome) === String(outcomeId)) {
          if (!pointsPossible) {
            pointsPossible = score.links.outcome_metadata.points_possible;
          }
          // Use pre-computed rounded values from score_metadata
          let rounded;
          if (roundingMethod === "roundHalfUp") {
            rounded = score.score_metadata?.round_half_up;
          } else {
            rounded = score.score_metadata?.round_down;
          }
          if (rounded != null) {
            roundedScores.add(Number(rounded));
          }
        }
      }
    }

    if (!pointsPossible) return undefined;

    return ratingScales
      .filter((scale) => {
        if (!scale.is_mapped) return false;
        if (!scale.levels || scale.levels.length === 0) return false;

        // System-generated scales (binary baseline) are always compatible
        if (scale.is_system_generated) return true;

        const levelPoints = new Set(scale.levels.map((l) => Number(l.points)));
        const maxPoints = Math.max(...levelPoints);

        // Max points must match points_possible
        if (maxPoints !== pointsPossible) return false;

        // Every rounded score must have a matching level in the scale
        for (const score of roundedScores) {
          if (!levelPoints.has(score)) return false;
        }

        return true;
      })
      .map((scale) => scale.id);
  }

  function buildMasteryMappingRow() {
    if (!formData.metadata.outcomeMetadata || !columns.length) {
      return [];
    }

    const scaleRow = {
      student: (
        <Text size="small" weight="bold">
          {t("finalizeCourse.ratingScaleSelection")}
        </Text>
      ),
    };

    // For each outcome column, add a RatingScaleSelect cell
    columns.forEach((col) => {
      if (col.key === "student") return;
      // Extract the outcome ID from the column key (format: "outcomeId_roundingMethod" or just "outcomeId")
      const outcomeId = col.key.split("_")[0];
      const compatibleMapIds = getCompatibleMapIds(outcomeId);

      scaleRow[col.key] = (
        <RatingScaleSelect
          title=""
          selectHandler={(mapId) => handleRatingScaleChange(outcomeId, mapId)}
          masteryMapping={
            formData.metadata.outcomeRatingScaleMap?.[outcomeId] || ""
          }
          mapIdsFilter={compatibleMapIds}
          includeSystemGenerated
          size="small"
          interaction={!!roundingMethod}
          clear={scaleSelectClearKey}
        />
      );
    });

    return [scaleRow];
  }

  // Add renderSummaryCell to columns so the pinned mastery mapping row renders
  function getColumnsWithPinnedRow() {
    return columns.map((col) => ({
      ...col,
      renderSummaryCell: ({ row }) => row[col.key] || null,
    }));
  }

  function getOutcomesWithNoScale() {
    const outcomes = formData.metadata.outcomeMetadata;
    if (!outcomes || !ratingScales) return [];
    return Object.keys(outcomes).filter((outcomeId) => {
      const compatibleIds = getCompatibleMapIds(outcomeId);
      return !compatibleIds || compatibleIds.length === 0;
    });
  }

  function autoSelectRatingScales(selectedRoundingMethod) {
    const outcomes = formData.metadata.outcomeMetadata;
    if (!outcomes || !ratingScales) return {};

    const autoMap = {};
    const rollupData = formData.metadata.results?.data || [];
    const sectionOutcomes = section.learning_outcomes || [];

    for (const outcomeId of Object.keys(outcomes)) {
      // Compute compatible scale IDs for this outcome with the given rounding
      const compatibleIds = getCompatibleMapIdsWithRounding(
        outcomeId,
        selectedRoundingMethod,
        rollupData
      );
      if (!compatibleIds || compatibleIds.length === 0) continue;

      const compatibleSet = new Set(compatibleIds);

      // Find the matching outcome from section.learning_outcomes (by canvas_id)
      const sectionOutcome = sectionOutcomes.find(
        (lo) =>
          String(lo.canvas_outcome_id || lo.canvas_id) === String(outcomeId)
      );

      let selected = null;

      // Priority 1: outcome's default rating scale (is_default === true)
      if (sectionOutcome?.rating_scales) {
        const defaultScale = sectionOutcome.rating_scales.find(
          (s) => s.is_default
        );
        if (
          defaultScale &&
          compatibleSet.has(defaultScale.rating_to_mastery_level_map)
        ) {
          selected = defaultScale.rating_to_mastery_level_map;
        }

        // Priority 2: any other scale associated with the outcome
        if (!selected) {
          for (const scale of sectionOutcome.rating_scales) {
            if (compatibleSet.has(scale.rating_to_mastery_level_map)) {
              selected = scale.rating_to_mastery_level_map;
              break;
            }
          }
        }
      }

      // Priority 3: institutional default map (is_default on ratingScales context)
      if (!selected) {
        const institutionalDefault = ratingScales.find(
          (s) => s.is_default && compatibleSet.has(s.id)
        );
        if (institutionalDefault) {
          selected = institutionalDefault.id;
        }
      }

      // Priority 4: first compatible scale
      if (!selected) {
        selected = compatibleIds[0];
      }

      if (selected) {
        autoMap[outcomeId] = selected;
      }
    }

    return autoMap;
  }

  function getCompatibleMapIdsWithRounding(
    outcomeId,
    selectedRounding,
    rollupData
  ) {
    // Same logic as getCompatibleMapIds but accepts rounding as a parameter
    // (needed because the state hasn't updated yet when called from handleChangeRounding)
    if (!ratingScales) return undefined;

    let pointsPossible = null;
    const roundedScores = new Set();

    for (const rollup of rollupData) {
      for (const score of rollup.scores || []) {
        if (String(score.links?.outcome) === String(outcomeId)) {
          if (!pointsPossible) {
            pointsPossible = score.links.outcome_metadata.points_possible;
          }
          // Use pre-computed rounded values from score_metadata
          let rounded;
          if (selectedRounding === "roundHalfUp") {
            rounded = score.score_metadata?.round_half_up;
          } else {
            rounded = score.score_metadata?.round_down;
          }
          if (rounded != null) {
            roundedScores.add(Number(rounded));
          }
        }
      }
    }

    if (!pointsPossible) return undefined;

    return ratingScales
      .filter((scale) => {
        if (!scale.is_mapped) return false;
        if (!scale.levels || scale.levels.length === 0) return false;

        // System-generated scales (binary baseline) are always compatible
        if (scale.is_system_generated) return true;

        const levelPoints = new Set(scale.levels.map((l) => Number(l.points)));
        const maxPoints = Math.max(...levelPoints);

        if (maxPoints !== pointsPossible) return false;

        for (const score of roundedScores) {
          if (!levelPoints.has(score)) return false;
        }

        return true;
      })
      .map((scale) => scale.id);
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        height: "100%",
      }}
    >
      <Heading border="bottom" level="h3" as="h2" width="100%">
        {t("finalizeCourse.learningMasteryGradebook")}
      </Heading>
      {rows.length > 0 && columns.length > 1 ? (
        <>
          <Flex gap="large">
            <Flex.Item align="start">
              <RadioInputGroup
                name="roundingMethod"
                description={t("finalizeCourse.selectRoundingMethod")}
                value={roundingMethod}
                onChange={handleChangeRounding}
              >
                <RadioInput
                  label={t("finalizeCourse.roundHalfUp")}
                  value="roundHalfUp"
                />
                <RadioInput
                  label={t("finalizeCourse.roundDown")}
                  value="roundDown"
                />
              </RadioInputGroup>
            </Flex.Item>
            <Flex.Item shouldGrow shouldShrink>
              <Card
                style={{ backgroundColor: "#F2F8FA", border: "1px solid #ccc" }}
              >
                <Flex direction="column" gap="small">
                  <Flex.Item>
                    <Text weight="bold">{t("finalizeCourse.roundHalfUp")}</Text>
                    <Text>{t("finalizeCourse.roundHalfUpDescription")}</Text>
                  </Flex.Item>
                  <Flex.Item>
                    <Text weight="bold">{t("finalizeCourse.roundDown")}</Text>
                    <Text>{t("finalizeCourse.roundDownDescription")}</Text>
                  </Flex.Item>
                </Flex>
              </Card>
            </Flex.Item>
          </Flex>
          <ToggleDetails
            size="small"
            summary={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                {staleOutcomes.length > 0 && (
                  <IconWarningSolid color="warning" size="x-small" />
                )}
                {t("finalizeCourse.pastOutcomeResults")}
              </span>
            }
          >
            <Text as="p" size="small">
              {t("finalizeCourse.pastOutcomeVersionDisclaimer")}
            </Text>
            {staleOutcomes.length > 0 && (
              <>
                <br />
                <List isUnstyled>
                  {staleOutcomes.map(({ name, description }) => (
                    <List.Item key={name}>
                      <Text size="small">
                        <Text size="small" weight="bold">
                          {name}:{" "}
                        </Text>
                        {description}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </>
            )}
          </ToggleDetails>
          {roundingMethod &&
            (() => {
              const outcomesWithNoScale = getOutcomesWithNoScale();
              return (
                outcomesWithNoScale.length > 0 && (
                  <Alert variant="warning">
                    {t("finalizeCourse.noCompatibleScale")}{" "}
                    <Text weight="bold">
                      {outcomesWithNoScale
                        .map(
                          (id) =>
                            formData.metadata.outcomeMetadata?.[id]?.title || id
                        )
                        .join(", ")}
                    </Text>
                  </Alert>
                )
              );
            })()}
          <DataGrid
            className="rdg-light"
            style={{ flex: "1", marginBottom: "0.05rem" }}
            rowHeight={60}
            headerRowHeight={40}
            columns={getColumnsWithPinnedRow()}
            rows={rows}
            topSummaryRows={buildMasteryMappingRow()}
          />
        </>
      ) : loading ? (
        <div style={{ flex: "1" }}>
          <Skeleton style={{ marginBottom: "0.75rem" }} height={100} />
          <Skeleton height="100%" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {t("notifications.general.fetchError", {
            obj: t("finalizeCourse.learningMasteryGradebook"),
          })}
        </Alert>
      ) : (
        <Alert>{t("finalizeCourse.noGradebookData")}</Alert>
      )}
    </div>
  );
};

export default LearningMasteryGradebook;
